// Spec 47 approval-ux — cmp_approval_reason_input / decision_reason_input.
//
// "Textarea for the approver's reason. Required on rejection (client and
// server enforced); optional on approval. Character counter, inline
// validation, and suggestion chips for common reasons (per subject_kind)."
// (spec 47 line 671)
//
// Spec 47 a11y:
//   - Label associated via <label for>; aria-required='true' when reject
//     is primed
//   - Character counter linked via aria-describedby
//   - Suggestion chips are buttons with aria-label='Use suggested reason:
//     {{text}}'
//   - Inline validation uses role='alert' on first error appearance

import { forwardRef, useId, useState, type TextareaHTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";

export interface ReasonInputSuggestion {
  readonly id: string;
  readonly label: string;
}

export interface ReasonInputProps
  extends Omit<TextareaHTMLAttributes<HTMLTextAreaElement>, "value" | "onChange"> {
  readonly value: string;
  readonly onChange: (next: string) => void;
  /** Visible label text. */
  readonly label: string;
  /** Promote required-on-reject — flips aria-required + shows required-indicator hint. */
  readonly rejectPrimed?: boolean;
  /** Maximum characters. Counter turns warning at maxLength - 50. */
  readonly maxLength?: number;
  readonly suggestions?: readonly ReasonInputSuggestion[];
  /** Visible-on-validation-failure error text. Renders role='alert'. */
  readonly errorText?: string;
  /** Override label for the required-on-reject helper hint. */
  readonly requiredHintLabel?: string;
  /** Override aria-label for the suggestion group. */
  readonly suggestionGroupLabel?: string;
}

export const ReasonInput = forwardRef<HTMLTextAreaElement, ReasonInputProps>(
  (
    {
      value,
      onChange,
      label,
      rejectPrimed = false,
      maxLength = 500,
      suggestions,
      errorText,
      requiredHintLabel = "Required for reject",
      suggestionGroupLabel = "Suggested reasons",
      className,
      placeholder,
      ...rest
    },
    ref,
  ) => {
    const id = useId();
    const counterId = `${id}-counter`;
    const errorId = `${id}-error`;
    const requiredHintId = `${id}-required-hint`;

    const [focused, setFocused] = useState(false);
    const remaining = maxLength - value.length;
    const counterIsWarning = remaining <= 50;

    // aria-describedby chain: counter + (optional) error + (optional)
    // required-indicator. Built by joining defined-only ids.
    const describedBy = [
      counterId,
      errorText !== undefined ? errorId : null,
      rejectPrimed ? requiredHintId : null,
    ]
      .filter((s): s is string => s !== null)
      .join(" ");

    return (
      <div
        data-component="reason-input-wrapper"
        className={cx("nai-reason-input-wrapper", className)}
      >
        {/* Label kept clean of required-mark glyphs — happy-dom + WAI-ARIA
            accessible-name computation includes aria-hidden text in the
            label string, breaking strict getByLabelText() lookups in tests.
            The required-hint span below ("Required for reject") + the
            textarea's aria-required="true" together carry the same signal. */}
        <label htmlFor={id} className="nai-reason-input-label" data-component="reason-input-label">
          {label}
        </label>
        <textarea
          ref={ref}
          id={id}
          value={value}
          onChange={(e) => onChange(e.target.value)}
          onFocus={() => setFocused(true)}
          onBlur={() => setFocused(false)}
          aria-required={rejectPrimed ? "true" : undefined}
          aria-describedby={describedBy.length > 0 ? describedBy : undefined}
          aria-invalid={errorText !== undefined ? "true" : undefined}
          maxLength={maxLength}
          rows={focused ? 4 : 1}
          data-component="reason-input-textarea"
          data-reject-primed={rejectPrimed || undefined}
          placeholder={placeholder}
          className="nai-reason-input-textarea"
          {...rest}
        />
        <div className="nai-reason-input-meta">
          <span
            id={counterId}
            data-component="reason-input-counter"
            data-warning={counterIsWarning || undefined}
            className={cx(
              "nai-reason-input-counter",
              counterIsWarning && "nai-reason-input-counter--warning",
            )}
          >
            {value.length}/{maxLength}
          </span>
          {rejectPrimed && (
            <span
              id={requiredHintId}
              data-component="reason-input-required-hint"
              className="nai-reason-input-required-hint"
            >
              {requiredHintLabel}
            </span>
          )}
        </div>
        {errorText !== undefined && (
          <div
            id={errorId}
            role="alert"
            data-component="reason-input-error"
            className="nai-reason-input-error"
          >
            {errorText}
          </div>
        )}
        {suggestions !== undefined && suggestions.length > 0 && (
          <div
            data-component="reason-input-suggestions"
            className="nai-reason-input-suggestions"
            role="group"
            aria-label={suggestionGroupLabel}
          >
            {suggestions.map((s) => (
              <button
                key={s.id}
                type="button"
                data-component="reason-input-suggestion-chip"
                className="nai-reason-input-suggestion-chip"
                aria-label={`Use suggested reason: ${s.label}`}
                onClick={() => onChange(s.label)}
              >
                {s.label}
              </button>
            ))}
          </div>
        )}
      </div>
    );
  },
);
ReasonInput.displayName = "ReasonInput";
